import { useState, useEffect, useCallback } from "react";

export type TimelineEvent = {
  id: string;
  title: string;
  description: string | null;
  date: string;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TimelineEra = {
  id: string;
  title: string;
  description: string | null;
  startDate: string;
  endDate: string;
  color: string;
  createdAt: string;
  updatedAt: string;
};

export type TimelinePerson = {
  id: string;
  name: string;
  birthDate: string | null;
  deathDate: string | null;
  bio: string | null;
  imageUrl: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
};

export type TimelineScene = {
  id: string;
  sceneCode: string;
  title: string;
  status: "draft" | "approved" | "killed" | "shot";
  firstImageUrl: string | null;
  computedDate: string | null;
  computedDateEnd: string | null;
  characters: { personId: string }[];
};

export function useTimelineData() {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [eras, setEras] = useState<TimelineEra[]>([]);
  const [people, setPeople] = useState<TimelinePerson[]>([]);
  const [scenes, setScenes] = useState<TimelineScene[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [eventsRes, erasRes, peopleRes, scenesRes] = await Promise.all([
        fetch("/api/timeline/events", { credentials: "include" }),
        fetch("/api/timeline/eras", { credentials: "include" }),
        fetch("/api/timeline/people", { credentials: "include" }),
        fetch("/api/scenes", { credentials: "include" }),
      ]);

      if (!eventsRes.ok || !erasRes.ok || !peopleRes.ok) {
        throw new Error("Failed to fetch timeline data");
      }

      const [eventsData, erasData, peopleData] = await Promise.all([
        eventsRes.json(),
        erasRes.json(),
        peopleRes.json(),
      ]);

      // Scenes fetch is optional - don't fail if it errors
      let scenesData = { scenes: [] };
      if (scenesRes.ok) {
        scenesData = await scenesRes.json();
      }

      setEvents(eventsData.events);
      setEras(erasData.eras);
      setPeople(peopleData.people);
      setScenes(scenesData.scenes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateEvent = async (id: string, data: Partial<TimelineEvent>) => {
    const res = await fetch(`/api/timeline/events/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!res.ok) throw new Error("Failed to update event");

    const { event } = await res.json();
    setEvents((prev) => prev.map((e) => (e.id === id ? event : e)));
    return event;
  };

  const updateEra = async (id: string, data: Partial<TimelineEra>) => {
    const res = await fetch(`/api/timeline/eras/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!res.ok) throw new Error("Failed to update era");

    const { era } = await res.json();
    setEras((prev) => prev.map((e) => (e.id === id ? era : e)));
    return era;
  };

  const updatePerson = async (id: string, data: Partial<TimelinePerson>) => {
    const res = await fetch(`/api/timeline/people/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!res.ok) throw new Error("Failed to update person");

    const { person } = await res.json();
    setPeople((prev) => prev.map((p) => (p.id === id ? person : p)));
    return person;
  };

  return {
    events,
    eras,
    people,
    scenes,
    isLoading,
    error,
    refetch: fetchData,
    updateEvent,
    updateEra,
    updatePerson,
  };
}
